import React from 'react';
import { DataCollectionGapSummary } from '../../../../store/Models/Api/DataCollectionGapSummaryModels';
import { useLocalizer } from '../hooks/useLocalizer';
import {
    getMaterialUnansweredCount,
    getQuestionnaireRequiredQuestionUnanswered,
    getRequiredQuestionsAnswered,
    getRequiredQuestionsTotal,
    hasMaterialGaps,
    hasQuestionnaireGaps
} from './helpers/gapSummaryHelpers';

interface GapSummarySectionProps {
    summaryPayload: DataCollectionGapSummary;
}

export const GapSummarySection: React.FC<GapSummarySectionProps> = ({
    summaryPayload
}) => {
    const localize = useLocalizer();
    const supplierQuestionnaireGaps = React.useMemo(() => {
        return summaryPayload.supplierQuestionnaires?.filter(hasQuestionnaireGaps) ?? [];
    }, [summaryPayload]);

    const materialGaps = React.useMemo(() => {
        return summaryPayload.materialData?.filter(hasMaterialGaps) ?? [];
    }, [summaryPayload]);

    const requiredQuestionsTotal = React.useMemo(
        () => getRequiredQuestionsTotal(summaryPayload),
        [summaryPayload]);

    const requiredQuestionsAnswered = React.useMemo(
        () => getRequiredQuestionsAnswered(summaryPayload),
        [summaryPayload]);

    const supplierRequiredQuestionUnanswered = React.useMemo(() => {
        return supplierQuestionnaireGaps.reduce(
            (total, questionnaire) => total + getQuestionnaireRequiredQuestionUnanswered(questionnaire),
            0);
    }, [supplierQuestionnaireGaps]);

    const materialRequiredQuestionUnanswered = React.useMemo(() => {
        return materialGaps.reduce(
            (total, material) => total + getMaterialUnansweredCount(material),
            0);
    }, [materialGaps]);

    const gapCopySegments = React.useMemo(() => {
        const segments: React.ReactNode[] = [];

        if (supplierRequiredQuestionUnanswered > 0) {
            const supplierQuestionLabel = supplierRequiredQuestionUnanswered === 1
                ? localize('supplier-level question')
                : localize('supplier-level questions');

            segments.push(
                <strong className="gap-summary-highlight" key="supplier">
                    {supplierRequiredQuestionUnanswered} {supplierQuestionLabel}
                </strong>
            );
        }

        if (materialRequiredQuestionUnanswered > 0) {
            const materialQuestionLabel = materialRequiredQuestionUnanswered === 1
                ? localize('material-level question')
                : localize('material-level questions');
            const materialLabel = materialGaps.length === 1
                ? localize('material')
                : localize('materials');

            segments.push(
                <React.Fragment key="material">
                    <strong className="gap-summary-highlight">
                        {materialRequiredQuestionUnanswered} {materialQuestionLabel}
                    </strong> {localize('across')} <strong>{materialGaps.length} {materialLabel}</strong>
                </React.Fragment>
            );
        }

        return segments;
    }, [
        localize,
        materialGaps.length,
        materialRequiredQuestionUnanswered,
        supplierRequiredQuestionUnanswered
    ]);

    return (
        <section className="gap-summary-callout gap-summary-overview">
            <div>
                <h4>{localize('Gap Summary')}</h4>
                <p>
                    {localize("We've answered")} <strong>{requiredQuestionsAnswered}/{requiredQuestionsTotal}</strong> {requiredQuestionsTotal === 1 ? localize('required question.') : localize('required questions.')}{' '}
                    {gapCopySegments.length === 0 && localize('All required questions were answered from the provided documents.')}
                    {gapCopySegments.length > 0 && (
                        <>
                            {gapCopySegments.map((segment, index) => (
                                <React.Fragment key={index}>
                                    {index > 0 && <> {localize('and')} </>}
                                    {segment}
                                </React.Fragment>
                            ))} {localize('could not be answered from the provided documents.')}
                        </>
                    )}
                </p>
                <p className="gap-summary-detail-note">
                    {localize('See question-level details below.')}
                </p>
            </div>
        </section>
    );
};

export default GapSummarySection;
